/**
 * 簡訊邀請「短連結」中繼頁 `/s/{token}` 的純函式。
 *
 * 背景：簡訊按字數計費，長邀請連結（`/sms-invite/open?liffId=…&phone=…&invite=…&order_id=…`）
 * 換成短碼 `https://…/s/Ab3xK9Qz`，實際參數改存後端（lpg-line-invite-sms-sqs），本頁用 token
 * 向後端換回參數，換到之後直接沿用既有 `/sms-invite/open` 的中繼邏輯（見 smsInviteOpen.ts 的
 * buildSmsInviteLiffUrl / buildSmsInviteBrowserPath）走完剩下流程。舊的長連結格式不受影響。
 *
 * 後端契約：GET `{REACT_APP_SMS_HTTP_BASE_URL}/s/{token}`（簡單 CORS GET，不能帶自訂 header，
 * 否則瀏覽器會先 preflight）：
 *   200 → { liff_id, invite, phone, k, order_id, src }（除 invite 外皆可能是 null）
 *   404 → { message: "not_found" }（token 不存在 / 過期 / 格式錯）
 */
import { SmsInviteOpenParams } from "./smsInviteOpen";

const DEFAULT_BASE = process.env.REACT_APP_SMS_HTTP_BASE_URL || "";
const RESOLVE_TIMEOUT_MS = 8000;

/** 短碼格式：固定 8 碼英數字（大小寫混合）。 */
export const isValidShortToken = (token: string): boolean => /^[A-Za-z0-9]{8}$/.test(token);

/**
 * 是否落在短連結中繼路徑 `/s/{token}`；比照 isSmsInviteOpenMode（smsInviteOpen.ts），
 * 供 LineLoginProvider 旁路判斷用（不初始化 LIFF、不導登入）。
 *
 * 故意不在這裡限定 token 格式——格式錯的 token 也要落在本頁，由畫面自己判斷、
 * 顯示「連結已失效」並導向既有的錯誤 fallback，而不是誤入正常 LIFF 初始化流程。
 */
export const isSmsShortLinkMode = (
  pathname: string = typeof window !== "undefined" ? window.location.pathname : "",
): boolean => /^\/s\/[^/]+\/?$/.test(pathname);

/** 後端 `/s/{token}` 200 回應結構。 */
export interface SmsShortLinkData {
  liff_id: string | null;
  invite: string;
  phone: string | null;
  k: string | null;
  order_id: string | null;
  src: string | null;
}

export type ResolveShortLinkResult =
  | { ok: true; data: SmsShortLinkData }
  | { ok: false; reason: "not_found" | "network" | "config" };

type FetchImpl = typeof fetch;

/**
 * 用短碼向後端換回落地參數。
 *   - 沒有 base URL（環境變數沒設）→ 'config'
 *   - 200 但缺 invite（資料異常）或明確 404 → 'not_found'（連結已失效，導客戶去要新連結）
 *   - 逾時（8 秒）或其他網路層錯誤 → 'network'（畫面給重試按鈕，不要直接把客戶彈走）
 */
export const resolveShortLink = async (
  token: string,
  baseUrl: string = DEFAULT_BASE,
  fetchImpl: FetchImpl = fetch,
): Promise<ResolveShortLinkResult> => {
  const base = (baseUrl || "").replace(/\/+$/, "");
  if (!base) return { ok: false, reason: "config" };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), RESOLVE_TIMEOUT_MS);
  try {
    const res = await fetchImpl(`${base}/s/${token}`, { signal: controller.signal });
    if (res.status === 404) return { ok: false, reason: "not_found" };
    if (!res.ok) return { ok: false, reason: "network" };
    const data = (await res.json()) as Partial<SmsShortLinkData> | null;
    if (!data || !data.invite) return { ok: false, reason: "not_found" };
    return {
      ok: true,
      data: {
        liff_id: data.liff_id ?? null,
        invite: data.invite,
        phone: data.phone ?? null,
        k: data.k ?? null,
        order_id: data.order_id ?? null,
        src: data.src ?? null,
      },
    };
  } catch {
    // AbortError（逾時）或連線層錯誤，一律當 network：讓畫面顯示「重新整理」而非判定連結失效
    return { ok: false, reason: "network" };
  } finally {
    clearTimeout(timer);
  }
};

const clean = (raw: string | null | undefined): string => (raw ?? "").trim();

/** 轉成既有中繼邏輯（smsInviteOpen.ts 的 buildSmsInviteLiffUrl / buildSmsInviteBrowserPath）吃的參數形狀；
 *  null / 空字串一律省略。 */
export const toRelayParams = (data: SmsShortLinkData): SmsInviteOpenParams => {
  const result: SmsInviteOpenParams = { liffId: clean(data.liff_id), invite: clean(data.invite) };
  const phone = clean(data.phone);
  if (phone) result.phone = phone;
  const k = clean(data.k);
  if (k) result.k = k;
  const orderId = clean(data.order_id);
  if (orderId) result.order_id = orderId;
  const src = clean(data.src);
  if (src) result.src = src;
  return result;
};
